import { useMemo, useRef } from 'react';
import Lottie from 'lottie-react';

import characterLetterBrown from '@/playlist/games/letterParty/assets/lottie/character-letter-brown.json';
import characterLetterGreen from '@/playlist/games/letterParty/assets/lottie/character-letter-green.json';
import characterLetterGrey from '@/playlist/games/letterParty/assets/lottie/character-letter-grey.json';

import type { CSSProperties } from 'react';

interface GridPosition {
	x: number;
	y: number;
}

interface LetterCharacterProps {
	letter: string;
	size?: number;
	position?: GridPosition;
	collected?: boolean;
	colorSeed?: number;
}

const CHARACTER_ANIMATIONS = [characterLetterBrown, characterLetterGreen, characterLetterGrey];

const updateLottieText = (animationData: any, text: string) => {
	// Use structuredClone if available (modern browsers), fallback to JSON method
	const clonedData =
		typeof structuredClone === 'function'
			? structuredClone(animationData)
			: JSON.parse(JSON.stringify(animationData));

	const textLayer = clonedData.layers?.find((layer: any) => layer.nm === 'LETTER');
	if (textLayer?.t?.d?.k?.[0]?.s) {
		textLayer.t.d.k[0].s.t = text;
	}

	return clonedData;
};

const LetterCharacter = ({
	letter,
	size = 45,
	position,
	collected,
	colorSeed = 0,
}: LetterCharacterProps) => {
	const bodySize = Math.max(20, size * 1.7);
	const lottieRef = useRef<any>(null);

	const animationData = useMemo(() => {
		const animationIndex = Math.abs(colorSeed) % CHARACTER_ANIMATIONS.length;
		const selectedAnimation = CHARACTER_ANIMATIONS[animationIndex];
		return updateLottieText(selectedAnimation, letter);
	}, [colorSeed, letter]);

	const wrapperStyle: CSSProperties = {
		width: size,
		height: size,
		left: (position?.x ?? 0) * size,
		top: (position?.y ?? 0) * size,
		zIndex: 100 + (position?.y ?? 0), // Higher y = higher z-index (bottom characters appear above)
	};

	if (collected) {
		return null;
	}

	return (
		<div className="letter-party-game__q-character" style={wrapperStyle}>
			<div className="letter-party-game__q-character-inner">
				<div
					className="letter-party-game__q-character-letter-wrapper"
					data-variant="default"
					style={{ width: bodySize, height: bodySize }}
				>
					<Lottie
						lottieRef={lottieRef}
						animationData={animationData}
						loop
						autoplay
						className="letter-party-game__q-character-letter-art"
					/>
				</div>
			</div>
		</div>
	);
};

export default LetterCharacter;
